import { callAuthedApi } from "@/services/serverService"
import { wishSelectors } from "@/slices/wishSlice"
import { waitMs } from "@/utils/timeUtils"
import { useReduxDispatch } from "@/store/useReduxDispatch"
import { Metadata } from "html-metadata-parser"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import "./editWishPage.scss"
import { updateWishThunk } from "@/thunks/wishThunks"
import { StandardButton } from "@/components/button/StandardButton"
import { getBackgroundPosition, getWishListForWish } from "@/services/clientWishService"
import { maxArray } from "@/utils/arrayUtils"
import { BasePage } from "@/components/layout/BasePage"
import { settingsActions } from "@/slices/settingsSlice"
import cn from "classnames"
import { formatLocalDateTime } from "@/utils/dateUtils"

const EditWishPage = (): JSX.Element => {
  const dispatch = useReduxDispatch()
  const navigate = useNavigate()

  const { id } = useParams()
  const idParam = parseInt(id ?? "0")

  const saveRef = useRef<() => Promise<void> | undefined>()
  saveRef.current = async () => {
    await dispatch(
      updateWishThunk(
        {
          id: wish?.id ?? 0,
          qty: parseInt(qty),
          title,
          url,
          description,
          imageUrl,
          isHidden: !!isHidden,
          sort: (idParam > 0 ? wish?.sort : undefined) ?? maxArray(wishes, (w) => w.sort, 0) + 1,
          regs: 0,
          myRegs: 0,
          isTitleBackgroundSolid: useSolidTitleBackground,
          imageFocus: imageFocus,
        },
        wishLists?.find((wl) => wl.owner === owner)?.id ?? 0
      )
    )
  }

  const wishLists = useSelector(wishSelectors.wishLists)

  const wishList = getWishListForWish(idParam, wishLists)
  const wish = wishList?.wishes.find((w) => w.id === idParam)
  const wishes = wishLists?.map((w) => w.wishes).flat() ?? []

  const [showRegs, setShowRegs] = useState<boolean>(false)
  const [url, setUrl] = useState<string>("")
  const [title, setTitle] = useState<string>(wish?.title ?? "")
  const [description, setDescription] = useState<string>(wish?.description ?? "")
  const [imageUrl, setImageUrl] = useState<string>(wish?.imageUrl ?? "")
  const [imageFocus, setImageFocus] = useState<EImageFocus>(wish?.imageFocus ?? "coverCenter")
  const [qty, setQty] = useState<string>(wish ? wish.qty + "" : "1")
  const [owner, setOwner] = useState<EOwner>(wishList?.owner ?? "marina")
  const [isHidden, setIsHidden] = useState<boolean>(wish?.isHidden ?? false)
  const [useSolidTitleBackground, setUseSolidTitleBackground] = useState<boolean>(
    wish?.isTitleBackgroundSolid ?? false
  )

  useEffect(() => {
    const handleSave = async (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "s") {
        e.preventDefault()
        await dispatch(settingsActions.setIsLoading(true))
        await Promise.all([saveRef.current?.(), waitMs(500)])
        await dispatch(settingsActions.setIsLoading(false))
      }
      if (e.ctrlKey && e.key === "b") {
        e.preventDefault()
        navigate("/")
      }
    }
    document.addEventListener("keydown", handleSave)
    return () => {
      document.removeEventListener("keydown", handleSave)
    }
  }, [])

  useEffect(() => {
    if (wish) {
      setUrl(wish.url ?? "")
      setTitle(wish.title)
      setDescription(wish.description ?? "")
      setImageUrl(wish.imageUrl ?? "")
      setQty(wish.qty + "")
      setOwner(wishList?.owner ?? "marina")
      setIsHidden(wish.isHidden)
      setUseSolidTitleBackground(wish.isTitleBackgroundSolid ?? false)
      setImageFocus(wish.imageFocus ?? "coverCenter")
    }
  }, [wish])

  return (
    <BasePage
      className="edit-wish-page"
      title="Opdater ønske"
      topRightContent={
        <div>
          <div>Gem: Ctrl + s</div>
          <div>Tilbage: Ctrl + b</div>
          <button onClick={() => navigate("/")}>Tilbage</button>
        </div>
      }
    >
      <div>
        <h3>{wish ? wish.title : "Nyt ønske"}</h3>
      </div>
      <Input
        title="Side URL"
        value={url}
        onChange={(val) => {
          setUrl(val)
        }}
        buttonTitle="Slå info op"
        buttonOnClick={async () => {
          const meta = await callAuthedApi<Metadata>("lookupPage", "post", { url })
          const title = meta.meta.title ?? meta.og.title
          const imageUrl =
            meta.meta.image ??
            meta.og.image ??
            (meta.images?.length ? (meta.images?.[0] as any)?.src : "")
          const description = meta.meta.description ?? meta.og.description
          if (title) {
            setTitle(title)
          }
          if (description) {
            setDescription(description)
          }
          if (imageUrl) {
            setImageUrl(imageUrl)
          }
        }}
      />

      <Input
        title="Titel"
        value={title}
        onChange={(val) => {
          setTitle(val)
        }}
      />
      <Input
        title="Beskrivelse"
        value={description}
        isTextArea
        onChange={(val) => {
          setDescription(val)
        }}
      />
      <InputDdl<EOwner>
        title="Ejer"
        value={owner}
        onChange={(val) => {
          setOwner(val)
        }}
        options={[
          { label: "Marina", value: "marina" },
          { label: "Christian", value: "christian" },
          { label: "Fælles", value: "common" },
        ]}
      />
      <Input
        title="Antal"
        value={qty + ""}
        onChange={(val) => {
          setQty(val)
        }}
      />
      <Input
        title="Er skjult?"
        value={isHidden ? "1" : ""}
        onChange={(val) => {
          setIsHidden(val === "1")
        }}
        isCheckbox
      />
      <Input
        title="Benyt ikke-gennemsigtig baggrund til titel"
        value={useSolidTitleBackground ? "1" : ""}
        onChange={(val) => {
          setUseSolidTitleBackground(val === "1")
        }}
        isCheckbox
      />
      <Input
        title="Billed URL"
        value={imageUrl}
        onChange={(val) => {
          setImageUrl(val)
        }}
      />
      <InputDdl<EImageFocus>
        title="Billedfokus"
        value={imageFocus}
        onChange={(val) => {
          setImageFocus(val)
        }}
        options={[
          { label: "Centrér", value: "coverCenter" },
          { label: "Indehold billede i rammen", value: "contain" },
          { label: "Øverst", value: "coverTop" },
          { label: "Nederst", value: "coverBottom" },
          { label: "Til venstre", value: "coverLeft" },
          { label: "Til højre", value: "coverRight" },
        ]}
      />
      <StandardButton
        className="edit-wish-page__save"
        title={wish ? "Opdater" : "Gem"}
        onClick={async () => await saveRef.current?.()}
      />
      {imageUrl && (
        <div style={{ backgroundColor: "white", display: "inline-block" }}>
          <div
            style={{
              width: 400,
              height: 400,
              backgroundImage: imageUrl ? `url("${imageUrl}")` : undefined,
              backgroundPosition: getBackgroundPosition(imageFocus),
              backgroundRepeat: "no-repeat",
              backgroundSize: imageFocus === "contain" ? "contain" : "cover",
            }}
          />
        </div>
      )}
      {!!wish?.registrations?.length && (
        <div className={cn("edit-wish-page__regs")}>
          <StandardButton
            className="edit-wish-page__regs-show"
            isRed
            title={!showRegs ? "Vis registreringer" : "Skjul registreringer"}
            onClick={async () => setShowRegs(!showRegs)}
          />
          {showRegs && (
            <div className="edit-wish-page__regs-list">
              {(wish?.registrations ?? []).map((wr, index) => (
                <div key={index}>
                  {wr.username}: {wr.qty} ({formatLocalDateTime(wr.created, "short", "short")})
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </BasePage>
  )
}

interface IInputProps {
  title: string
  value: string
  onChange: (val: string) => void
  isTextArea?: boolean
  isCheckbox?: boolean
  isInt?: boolean
  buttonTitle?: string
  buttonOnClick?: () => Promise<void>
}
const Input = ({
  title,
  value,
  onChange,
  isTextArea,
  isInt,
  isCheckbox,
  buttonTitle,
  buttonOnClick,
}: IInputProps): JSX.Element => {
  return (
    <div className="edit-wish-page__input">
      <span>{title}:</span>
      {isTextArea && <textarea value={value} onChange={(e) => onChange(e.currentTarget.value)} />}
      {!isTextArea && (
        <input
          type={isInt ? "number" : isCheckbox ? "checkbox" : "text"}
          value={value}
          onChange={(e) =>
            onChange(isCheckbox ? (e.currentTarget.checked ? "1" : "") : e.currentTarget.value)
          }
          checked={isCheckbox && value === "1"}
        />
      )}
      {buttonOnClick && <button onClick={async () => await buttonOnClick()}>{buttonTitle}</button>}
    </div>
  )
}

interface IInputDdlProps<T> {
  title: string
  value: string
  onChange: (val: T) => void
  options: { label: string; value: T }[]
  buttonTitle?: string
  buttonOnClick?: () => Promise<void>
}
const InputDdl = <T extends string>({
  title,
  value,
  options,
  onChange,
}: IInputDdlProps<T>): JSX.Element => {
  return (
    <div className="edit-wish-page__input-ddl">
      <span>{title}:</span>

      <select value={value} onChange={(e) => onChange(e.currentTarget.value as T)}>
        {options.map((o) => (
          <option value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

export default EditWishPage
